import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { track } from "../lib/analytics";

const QUESTIONS = [
  {
    id: "Visibility",
    title: "Where is your product currently sold?",
    options: [
      { label: "Just social media/Direct to friends", score: 10 },
      {
        label: "Established local retail stores/e-commerce platforms",
        score: 20,
      },
      {
        label:
          "Premium retail or corporate partnerships (e.g., MedPlus, Hotels)",
        score: 30,
      },
    ],
  },
  {
    id: "identity",
    title: "How would you describe your brand’s visual identity?",
    options: [
      { label: "It’s a work in progress; we focus on the product", score: 5 },
      {
        label: "Professional, but needs an upgrade for international shelves",
        score: 15,
      },
      {
        label: "World-class; ready to sit next to global brands today",
        score: 25,
      },
    ],
  },
  {
    id: "independence",
    title:
      "If you were away for a month, could your production continue without you?",
    options: [
      { label: "No, I manage every detail myself", score: 5 },
      {
        label: "Mostly, but I still make the major operational decisions",
        score: 15,
      },
      {
        label: "Yes, we have documented SOPs and a management team in place",
        score: 30,
      },
    ],
  },
  {
    id: "legal",
    title: "What is your current legal and regulatory status?",
    options: [
      { label: "Registered locally (CAC) only", score: 5 },
      { label: "Registered locally with NAFDAC/SON approvals", score: 15 },
      {
        label:
          "Registered locally + international incorporation (US/UK) or FDA/ISO ready",
        score: 30,
      },
    ],
  },
  {
    id: "finances",
    title: "How do you track your business finances?",
    options: [
      { label: " Personal bank account and basic records", score: 5 },
      {
        label: " Separate business account with manual/spreadsheet tracking",
        score: 15,
      },
      {
        label:
          "Professional accounting software (QuickBooks/KostPadi) with clean data",
        score: 30,
      },
    ],
  },
  {
    id: "Scalability",
    title:
      "If a global retailer ordered 5,000 units tomorrow, what would happen?",
    options: [
      {
        label: "I would have to decline; I don't have the funds or space",
        score: 5,
      },
      {
        label:
          "I could do it, but it would take months to source materials and capital ",
        score: 15,
      },
      {
        label:
          " I have the supply chain and just need the inventory funding to pull the trigger",
        score: 30,
      },
    ],
  },
  {
    id: "documentation",
    title: "Business documentation",
    options: [
      { label: "None", score: 5 },
      { label: "Some documents", score: 15 },
      { label: "Full documents (registration, finance, ops)", score: 30 },
    ],
  },
];

function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}

export default function DiagnosticTest({ onOpenModal }) {
  const totalSteps = QUESTIONS.length;
  const maxScore = totalSteps * 30;

  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [secondsLeft, setSecondsLeft] = useState(60);
  const [done, setDone] = useState(false);

  const current = QUESTIONS[step];

  const score = useMemo(() => {
    let s = 0;
    for (const q of QUESTIONS) {
      const picked = answers[q.id];
      if (typeof picked === "number") s += picked;
    }
    return s;
  }, [answers]);

  const percent = useMemo(() => {
    return Math.round((score / maxScore) * 100);
  }, [score, maxScore]);

  const progress = useMemo(() => {
    return Math.round(((step + 1) / totalSteps) * 100);
  }, [step, totalSteps]);

  const tier = useMemo(() => {
    if (!done) return null;
    if (percent >= 75)
      return {
        title: "Export-ready signal",
        note: "You look close to showcase-ready. The next step is tightening compliance and fulfillment.",
      };
    if (percent >= 45)
      return {
        title: "Nearly ready",
        note: "You have traction. You need structure plus compliance upgrades to reach export-grade.",
      };
    return {
      title: "Foundation stage",
      note: "You need structured ops, packaging, and quality controls before global buyers.",
    };
  }, [done, percent]);

  useEffect(() => {
    if (done) return;

    const t = window.setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          window.clearInterval(t);
          setDone(true);
          track("diagnostic_timeout", { score });
          return 0;
        }
        return s - 1;
      });
    }, 1000);

    return () => window.clearInterval(t);
  }, [done, score]);

  function pick(optionScore) {
    setAnswers((prev) => ({ ...prev, [current.id]: optionScore }));
  }

  function next() {
    if (typeof answers[current.id] !== "number") return;
    if (step >= totalSteps - 1) {
      setDone(true);
      track("diagnostic_complete", { score: score, percent });
      return;
    }
    setStep((s) => s + 1);
  }

  function back() {
    setStep((s) => clamp(s - 1, 0, totalSteps - 1));
  }

  return (
    <div className="diagPage">
      <div className="container">
        <div className="diagShell">
          <div className="diagTop">
            <Link className="backLink" to="/">
              ← Back
            </Link>
            <div className="diagTimer" aria-label="Time left">
              <span className="diagTimer__label">Time</span>
              <span
                className={`diagTimer__value ${
                  secondsLeft <= 10 ? "diagTimer__value--hot" : ""
                }`}
              >
                0:{String(secondsLeft).padStart(2, "0")}
              </span>
            </div>
          </div>

          <div className="diagHeader">
            <h1 className="diagTitle">
              SmartUp360: The Export-Ready & Capacity Audit
            </h1>
            <p className="diagSub">
              Answer fast. Get a clear next step. Then apply for the next Global
              Showcase.
            </p>
          </div>

          <div className="diagBar">
            <div className="diagBar__fill" style={{ width: `${progress}%` }} />
          </div>

          {!done ? (
            <div className="diagCard">
              <div className="diagCard__meta">
                <span className="diagStep">
                  Question {step + 1} of {totalSteps}
                </span>
                <span className="diagScore">
                  Score {score}/{maxScore}
                </span>
              </div>

              <div className="diagQ">{current.title}</div>

              <div
                className="diagOpts"
                role="radiogroup"
                aria-label={current.title}
              >
                {current.options.map((o) => {
                  const active = answers[current.id] === o.score;
                  return (
                    <button
                      key={o.label}
                      type="button"
                      className={`diagOpt ${active ? "diagOpt--active" : ""}`}
                      onClick={() => pick(o.score)}
                      aria-pressed={active}
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>

              <div className="diagActions">
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={back}
                  disabled={step === 0}
                >
                  Back
                </button>
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={next}
                  disabled={typeof answers[current.id] !== "number"}
                >
                  {step === totalSteps - 1 ? "Finish" : "Next"}
                </button>
              </div>
            </div>
          ) : (
            <div className="diagCard">
              <div className="diagResultTop">
                <div className="diagResultTitle">
                  {tier ? tier.title : "Result"}
                </div>
                <div className="diagResultScore">{percent}% readiness</div>
              </div>

              <div className="diagResultNote">{tier ? tier.note : ""}</div>

              <div className="diagResultGrid">
                <div className="diagStat">
                  <div className="diagStat__label">Score</div>
                  <div className="diagStat__value">
                    {score}/{maxScore}
                  </div>
                </div>
                <div className="diagStat">
                  <div className="diagStat__label">Next</div>
                  <div className="diagStat__value">Waitlist and review</div>
                </div>
                <div className="diagStat">
                  <div className="diagStat__label">Path</div>
                  <div className="diagStat__value">Showcase fit</div>
                </div>
              </div>

              <div className="diagActions">
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={() => {
                    track("diagnostic_apply_click", { score, percent });
                    onOpenModal("hero_primary");
                  }}
                >
                  Apply for the Next Global Showcase
                </button>

                <button
                  type="button"
                  className="btn btn--secondary"
                  onClick={() => {
                    track("diagnostic_waitlist_click", { score, percent });
                    onOpenModal("hero_secondary");
                  }}
                >
                  Join the 2026 Platform Waitlist
                </button>
              </div>

              <div className="diagFoot">
                Your SmartUp Trust Score grows after waitlist submission and
                platform activity.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
